import React, { useState } from "react";
import { Check, Briefcase, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/site/Reveal";
import { api } from "@/lib/api";

const inputCls = "w-full bg-white rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:ring-2 focus:ring-brand focus:border-transparent";
const lbl = "block text-sm font-semibold text-navy mb-2";

function AcademyForm({ a }) {
  const [d, setD] = useState({ full_name: "", email: "", phone: "", preferred_language: "", message: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [showErr, setShowErr] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const valid = d.full_name.trim() && emailValid && d.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setShowErr(true); return; }
    setStatus("sending");
    try {
      await api.post("/academy-inquiries", { ...d, full_name: d.full_name.trim(), phone: d.phone.trim() });
      setStatus("success");
      document.getElementById("academy-form").scrollIntoView({ behavior: "smooth", block: "center" });
    } catch { setStatus("error"); }
  };

  if (status === "success") {
    return (
      <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-8 md:p-10 text-center" data-testid="academy-form-success">
        <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-5 bg-brand text-white"><CheckCircle2 size={36} /></div>
        <h3 className="font-heading font-extrabold text-2xl text-navy mb-3">{a.fSuccessTitle}</h3>
        <p className="text-ink/70 leading-relaxed">{a.fSuccessBody}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="bg-white rounded-3xl border border-navy/5 shadow-sm p-6 md:p-8 space-y-4" data-testid="academy-form-el">
      {showErr && !valid && <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="academy-form-error">{a.fErr}</div>}
      <div><label className={lbl}>{a.fName} *</label><input data-testid="academy-name" className={inputCls} value={d.full_name} onChange={(e) => setD({ ...d, full_name: e.target.value })} /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={lbl}>{a.fEmail} *</label><input type="email" data-testid="academy-email" className={inputCls} value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />{showErr && !emailValid && <p className="text-xs text-red-500 mt-1">{a.fEmailErr}</p>}</div>
        <div><label className={lbl}>{a.fPhone} <span className="font-normal text-slate-400">{a.optionalTxt}</span></label><input type="tel" data-testid="academy-phone" className={inputCls} value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} /></div>
      </div>
      <div><label className={lbl}>{a.fLang} <span className="font-normal text-slate-400">{a.optionalTxt}</span></label>
        <select data-testid="academy-language" className={inputCls} value={d.preferred_language} onChange={(e) => setD({ ...d, preferred_language: e.target.value })}>
          <option value="">{a.fLangOptions[0]}</option>
          <option value="English">{a.fLangOptions[1]}</option>
          <option value="Spanish">{a.fLangOptions[2]}</option>
        </select>
      </div>
      <div><label className={lbl}>{a.fMessage} <span className="font-normal text-slate-400">{a.optionalTxt}</span></label><textarea data-testid="academy-message" rows={4} className={inputCls} value={d.message} onChange={(e) => setD({ ...d, message: e.target.value })} /></div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="academy-consent">
        <input type="checkbox" checked={d.consent} onChange={(e) => setD({ ...d, consent: e.target.checked })} className="mt-1 w-5 h-5 accent-[#0F2D52]" />
        <span className="text-sm font-medium text-ink/80">{a.fConsent} *</span>
      </label>
      {status === "error" && <p className="text-red-500 text-center">{a.fGeneric}</p>}
      <button type="submit" data-testid="academy-submit" disabled={status === "sending"} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-extrabold text-white text-lg shadow-xl disabled:opacity-60 bg-navy hover:bg-brand transition-colors">
        <Send size={20} /> {status === "sending" ? a.fSubmitting : a.fSubmit}
      </button>
    </form>
  );
}

export default function Academy() {
  const { tr } = useLang();
  const a = tr.academy;

  return (
    <div data-testid="academy-page">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-navy text-white grain">
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl bg-white/10 text-gold grid place-items-center mx-auto mb-6"><Briefcase size={26} /></div>
            <Kicker dark>{a.kicker}</Kicker>
            <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.05] mb-5">{a.title}</h1>
            <p className="text-lg md:text-xl text-gold font-semibold mb-6">{a.subtitle}</p>
            <a href="#academy-form" data-testid="academy-hero-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-8 py-4 font-bold hover:brightness-105 transition">
              {a.requestInfo}
            </a>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="text-lg md:text-xl text-ink/75 leading-relaxed">{a.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* STEPS */}
      <section className="pb-8 lg:pb-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Kicker>{a.stepsTitle}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-3xl md:text-5xl tracking-tight">{a.stepsTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {a.steps.map((step, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div data-testid={`academy-step-${i}`} className="bg-white rounded-3xl p-8 border border-navy/5 h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-11 h-11 shrink-0 rounded-2xl bg-brand text-white grid place-items-center font-heading font-extrabold">{i + 1}</span>
                    <h3 className="font-heading font-bold text-xl text-navy leading-tight">{step.title}</h3>
                  </div>
                  <ul className="space-y-2.5">
                    {step.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-ink/70"><Check size={16} className="text-gold mt-0.5 shrink-0" />{it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OPTIONS + NOTE */}
      <section className="py-20 lg:py-28 bg-aqua/40">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <div className="bg-white rounded-[2rem] p-10 md:p-12 border border-navy/5">
              <Kicker>{a.optionsTitle}</Kicker>
              <h2 className="font-heading font-extrabold text-navy text-3xl md:text-4xl tracking-tight mb-7">{a.optionsTitle}</h2>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                {a.options.map((opt, i) => (
                  <li key={i} className="flex items-start gap-2 text-ink/75"><Check size={18} className="text-brand mt-0.5 shrink-0" />{opt}</li>
                ))}
              </ul>
              <p className="text-sm text-ink/60 bg-offwhite rounded-2xl p-5 border border-navy/5" data-testid="academy-note">{a.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REQUEST INFO FORM */}
      <section id="academy-form" className="py-20 lg:py-28">
        <div className="mx-auto max-w-2xl px-5 lg:px-8">
          <Reveal className="text-center mb-8">
            <Kicker>{a.kicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-3xl md:text-4xl tracking-tight mb-3">{a.formTitle}</h2>
            <p className="text-ink/70 text-lg">{a.formSubtitle}</p>
          </Reveal>
          <Reveal delay={0.05}><AcademyForm a={a} /></Reveal>
        </div>
      </section>
    </div>
  );
}
